/**
 * Where the sun and the moon actually are.
 *
 * CLAUDE.md asks for "real sun position from lat/lon/date/time", and the reason to do it
 * properly rather than to spin a light around is that everything downstream inherits the
 * correctness for free: the sun rises in the right place for the latitude, the days are the
 * right length for the season, the sun tracks north of the zenith in the southern hemisphere,
 * the shadows fall the right way at a given local time, and a dusk landing at Innsbruck in
 * January has the sun setting behind the right ridge. None of that can be faked convincingly,
 * and all of it is about fifty lines of astronomy.
 *
 * ## Accuracy, and why this level of it
 *
 * The sun uses the USNO low-precision formulae: better than 0.01° over 1950–2050. The moon uses
 * the truncated ELP series everyone uses for this job: about 0.3° in longitude. For scale, the
 * sun and moon are both about 0.5° across, so the sun is placed to a fiftieth of its own
 * diameter and the moon to about half of one. A full VSOP87/ELP2000 would be three orders of
 * magnitude more code for an error already well below one pixel of sun disc.
 *
 * Deliberately *not* modelled: nutation, aberration, atmospheric refraction, parallax and the
 * equation of the equinoxes. Refraction is the largest of these — it lifts a body near the
 * horizon by about 0.57°, which is why the sun is fully visible at the moment it is
 * geometrically already set. It is left out of the position and folded into the *rendering*
 * instead, where it belongs, because it is an atmospheric effect and the atmosphere already
 * knows the elevation angle.
 *
 * ## Conventions
 *
 * Angles in **degrees** at the boundary (this is astronomy, and every reference is in degrees),
 * radians internally. Azimuth is measured **from true north, increasing toward the east** — the
 * navigator's convention, so it can be compared directly with a runway heading. Elevation is
 * above the true horizon. Time is always UTC; local time is the caller's problem.
 *
 * `updateFixed` / `updateFrame`: neither. These are pure functions of a timestamp, called by the
 * environment when its clock moves — a few times a second at most while the slider is dragged,
 * and once every few seconds otherwise.
 */

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/** Julian date of the J2000.0 epoch, 2000-01-01T12:00:00Z. */
export const J2000 = 2_451_545.0;

/** Mean length of a synodic month [days] — new moon to new moon. */
export const SYNODIC_MONTH_DAYS = 29.530_588_9;

/** A body's position in the sky, as seen from somewhere on the ground. */
export interface HorizontalPosition {
  /** Degrees above the true horizon; negative when below it. */
  elevationDeg: number;
  /** Degrees from true north, increasing eastward, 0…360. */
  azimuthDeg: number;
}

/** A body's position on the celestial sphere. */
export interface EquatorialPosition {
  /** Right ascension [deg], 0…360. */
  rightAscensionDeg: number;
  /** Declination [deg], −90…90. */
  declinationDeg: number;
  /** Distance [km]. Meaningless for the sun's unit-less use, but the moon's phase needs it. */
  distanceKm: number;
}

/** Everything about the moon that a renderer needs. */
export interface MoonState extends HorizontalPosition {
  /** Fraction of the disc that is lit, 0 (new) … 1 (full). */
  illuminatedFraction: number;
  /**
   * Position in the cycle, 0 = new, 0.25 = first quarter, 0.5 = full, 0.75 = last quarter.
   *
   * This is what tells the renderer *which side* is lit — `illuminatedFraction` alone cannot,
   * because a waxing and a waning crescent are equally bright and mirror images of each other.
   */
  phase: number;
  /**
   * Position angle of the bright limb [deg], measured from celestial north through east.
   * The direction the lit side faces, which is what actually orients the terminator.
   */
  brightLimbAngleDeg: number;
  /** Angular radius of the disc as seen from Earth [deg]; varies ±6 % over a month. */
  angularRadiusDeg: number;
}

/** Julian date from a JavaScript timestamp. */
export function julianDate(utc: Date): number {
  return utc.getTime() / 86_400_000 + 2_440_587.5;
}

/** Days since J2000.0, the argument every series below is written in. */
export function daysSinceJ2000(utc: Date): number {
  return julianDate(utc) - J2000;
}

/**
 * Greenwich mean sidereal time [deg].
 *
 * Sidereal time is the hour angle of the vernal equinox — how far the *sky* has rotated, as
 * opposed to how far the sun has. They differ by about four minutes a day, which is why the
 * stars rise four minutes earlier each night and why a star field driven by solar time slowly
 * and unmistakably drifts out of season.
 */
export function greenwichMeanSiderealTimeDeg(utc: Date): number {
  const d = daysSinceJ2000(utc);
  // 18.697374558 h at J2000, advancing 24.06570982441908 h per day (one extra rotation a year).
  const hours = 18.697_374_558 + 24.065_709_824_419_08 * d;
  return normaliseDeg(hours * 15);
}

/** Local mean sidereal time [deg] at a longitude (east positive). */
export function localSiderealTimeDeg(utc: Date, longitudeDeg: number): number {
  return normaliseDeg(greenwichMeanSiderealTimeDeg(utc) + longitudeDeg);
}

/** Mean obliquity of the ecliptic [deg] — the tilt that gives us seasons. */
export function obliquityDeg(daysFromJ2000: number): number {
  return 23.439_291 - 3.563e-7 * daysFromJ2000;
}

/**
 * The sun's geocentric equatorial position.
 *
 * USNO's low-precision algorithm: mean longitude, mean anomaly, one equation-of-centre term plus
 * its first harmonic, then a straight ecliptic-to-equatorial rotation. The two sine terms are
 * the whole of the correction for Earth's orbit being an ellipse rather than a circle, and
 * between them they are also the whole of the equation of time — the reason solar noon wanders
 * ±16 minutes either side of clock noon over a year.
 */
export function sunEquatorial(utc: Date): EquatorialPosition {
  const d = daysSinceJ2000(utc);

  const meanLongitude = normaliseDeg(280.460 + 0.985_647_4 * d);
  const meanAnomaly = normaliseDeg(357.528 + 0.985_600_3 * d) * DEG;

  const eclipticLongitude =
    (meanLongitude + 1.915 * Math.sin(meanAnomaly) + 0.020 * Math.sin(2 * meanAnomaly)) * DEG;
  const epsilon = obliquityDeg(d) * DEG;

  const sinLambda = Math.sin(eclipticLongitude);
  const cosLambda = Math.cos(eclipticLongitude);

  return {
    rightAscensionDeg: normaliseDeg(Math.atan2(Math.cos(epsilon) * sinLambda, cosLambda) * RAD),
    declinationDeg: Math.asin(Math.sin(epsilon) * sinLambda) * RAD,
    // 1 AU, modulated by the eccentricity of Earth's orbit. Only used for the moon's phase.
    distanceKm: 149_597_870.7 * (1.000_14 - 0.016_71 * Math.cos(meanAnomaly)),
  };
}

/** The sun's ecliptic longitude [deg] — needed on its own for the moon's elongation. */
function sunEclipticLongitudeDeg(d: number): number {
  const meanLongitude = normaliseDeg(280.460 + 0.985_647_4 * d);
  const meanAnomaly = normaliseDeg(357.528 + 0.985_600_3 * d) * DEG;
  return normaliseDeg(
    meanLongitude + 1.915 * Math.sin(meanAnomaly) + 0.020 * Math.sin(2 * meanAnomaly),
  );
}

/** Where the sun is in the sky, from a point on the ground. */
export function sunPosition(
  utc: Date,
  latitudeDeg: number,
  longitudeDeg: number,
): HorizontalPosition {
  const equatorial = sunEquatorial(utc);
  return equatorialToHorizontal(equatorial, utc, latitudeDeg, longitudeDeg);
}

/**
 * The moon's geocentric ecliptic position, from the standard truncated lunar series.
 *
 * Four terms in longitude, one in latitude, two in distance. The moon's orbit is the most
 * perturbed thing in the solar system that anyone tries to compute by hand — the full theory
 * runs to thousands of terms — but the big ones are recognisable: the *evection* and *variation*
 * are the sun pulling the moon's orbit around as the Earth–moon line rotates, and together with
 * the elliptical term they account for nearly all of the several degrees of wobble.
 */
function moonEcliptic(d: number): { longitudeDeg: number; latitudeDeg: number; distanceKm: number } {
  // Mean elements.
  const L = normaliseDeg(218.316 + 13.176_396 * d); // mean longitude
  const M = normaliseDeg(134.963 + 13.064_993 * d) * DEG; // mean anomaly
  const F = normaliseDeg(93.272 + 13.229_350 * d) * DEG; // argument of latitude
  const D = normaliseDeg(297.850 + 12.190_749 * d) * DEG; // mean elongation from the sun
  const Msun = normaliseDeg(357.528 + 0.985_600_3 * d) * DEG; // the sun's mean anomaly

  const longitudeDeg = normaliseDeg(
    L +
      6.289 * Math.sin(M) + // elliptical orbit
      1.274 * Math.sin(2 * D - M) + // evection
      0.658 * Math.sin(2 * D) + // variation
      -0.186 * Math.sin(Msun) + // annual equation
      -0.059 * Math.sin(2 * M - 2 * D) +
      -0.057 * Math.sin(M - 2 * D + Msun),
  );

  const latitudeDeg = 5.128 * Math.sin(F) + 0.281 * Math.sin(M + F) - 0.278 * Math.sin(F - M);

  const distanceKm = 385_001 - 20_905 * Math.cos(M) - 3_699 * Math.cos(2 * D - M) - 2_956 * Math.cos(2 * D);

  return { longitudeDeg, latitudeDeg, distanceKm };
}

/** The moon's geocentric equatorial position. */
export function moonEquatorial(utc: Date): EquatorialPosition {
  const d = daysSinceJ2000(utc);
  const { longitudeDeg, latitudeDeg, distanceKm } = moonEcliptic(d);
  const epsilon = obliquityDeg(d) * DEG;
  const lambda = longitudeDeg * DEG;
  const beta = latitudeDeg * DEG;

  const sinLambda = Math.sin(lambda);
  const cosLambda = Math.cos(lambda);
  const sinBeta = Math.sin(beta);
  const cosBeta = Math.cos(beta);

  return {
    rightAscensionDeg: normaliseDeg(
      Math.atan2(sinLambda * Math.cos(epsilon) - (sinBeta / cosBeta) * Math.sin(epsilon), cosLambda) *
        RAD,
    ),
    declinationDeg:
      Math.asin(sinBeta * Math.cos(epsilon) + cosBeta * Math.sin(epsilon) * sinLambda) * RAD,
    distanceKm,
  };
}

/**
 * Everything about the moon: where it is, how much of it is lit, and which way round.
 *
 * The phase comes from the *elongation* — the angle between the sun and the moon as seen from
 * Earth. At 0° the moon is between us and the sun and its lit face points away: new. At 180° we
 * are between them and we see the whole lit face: full. The illuminated fraction is
 * `(1 + cos i)/2` where `i` is the phase angle at the moon, which is what an observer at the
 * moon's terminator would call the sun's elevation.
 */
export function moonState(utc: Date, latitudeDeg: number, longitudeDeg: number): MoonState {
  const d = daysSinceJ2000(utc);
  const moon = moonEcliptic(d);
  const moonEq = moonEquatorial(utc);
  const sunEq = sunEquatorial(utc);
  const sunLongitudeDeg = sunEclipticLongitudeDeg(d);

  const horizontal = equatorialToHorizontal(moonEq, utc, latitudeDeg, longitudeDeg);

  // Elongation: the angle sun–Earth–moon, from the spherical law of cosines on the ecliptic.
  const elongation = Math.acos(
    clamp(Math.cos(moon.latitudeDeg * DEG) * Math.cos((moon.longitudeDeg - sunLongitudeDeg) * DEG), -1, 1),
  );

  // The phase angle at the moon. The arctangent form (rather than simply π − elongation) is what
  // accounts for the sun being a finite distance away; it matters by about a tenth of a percent
  // of illuminated fraction, and it costs one divide.
  const phaseAngle = Math.atan2(
    sunEq.distanceKm * Math.sin(elongation),
    moon.distanceKm - sunEq.distanceKm * Math.cos(elongation),
  );
  const illuminatedFraction = (1 + Math.cos(phaseAngle)) / 2;

  // 0 = new, 0.5 = full. Taken from the ecliptic longitude difference, which — unlike the
  // illuminated fraction — increases monotonically through the month and so distinguishes a
  // waxing crescent from a waning one.
  const phase = normaliseDeg(moon.longitudeDeg - sunLongitudeDeg) / 360;

  // Position angle of the bright limb: it points directly away from the sun on the sky.
  const dRa = (sunEq.rightAscensionDeg - moonEq.rightAscensionDeg) * DEG;
  const sunDec = sunEq.declinationDeg * DEG;
  const moonDec = moonEq.declinationDeg * DEG;
  const brightLimbAngleDeg = normaliseDeg(
    Math.atan2(
      Math.cos(sunDec) * Math.sin(dRa),
      Math.sin(sunDec) * Math.cos(moonDec) - Math.cos(sunDec) * Math.sin(moonDec) * Math.cos(dRa),
    ) * RAD,
  );

  // The moon's mean radius is 1737.4 km; the disc is noticeably bigger at perigee than apogee,
  // which is the whole of the "supermoon" phenomenon.
  const angularRadiusDeg = Math.asin(1_737.4 / moon.distanceKm) * RAD;

  return {
    ...horizontal,
    illuminatedFraction,
    phase,
    brightLimbAngleDeg,
    angularRadiusDeg,
  };
}

/**
 * Equatorial (RA/Dec) to horizontal (azimuth/elevation) for an observer.
 *
 * The one place a sign error hides for weeks, so, explicitly: the hour angle `H` is how far the
 * body is *past* the meridian, so `H < 0` means it has not got there yet and is therefore in the
 * east — and the `−cos δ sin H` in the sine-of-azimuth term is what turns that into an azimuth
 * measured from north toward east.
 */
export function equatorialToHorizontal(
  equatorial: EquatorialPosition,
  utc: Date,
  latitudeDeg: number,
  longitudeDeg: number,
): HorizontalPosition {
  const hourAngle =
    (localSiderealTimeDeg(utc, longitudeDeg) - equatorial.rightAscensionDeg) * DEG;
  const declination = equatorial.declinationDeg * DEG;
  const latitude = latitudeDeg * DEG;

  const sinDec = Math.sin(declination);
  const cosDec = Math.cos(declination);
  const sinLat = Math.sin(latitude);
  const cosLat = Math.cos(latitude);
  const sinH = Math.sin(hourAngle);
  const cosH = Math.cos(hourAngle);

  const sinElevation = clamp(sinDec * sinLat + cosDec * cosLat * cosH, -1, 1);
  const azimuth = Math.atan2(-cosDec * sinH, sinDec * cosLat - cosDec * sinLat * cosH);

  return {
    elevationDeg: Math.asin(sinElevation) * RAD,
    azimuthDeg: normaliseDeg(azimuth * RAD),
  };
}

/**
 * Atmospheric refraction [deg] to add to a geometric elevation.
 *
 * Bennett's formula. Worth having because it is not small where it matters: at the geometric
 * horizon it is 0.57°, slightly more than the sun's own diameter — so the sun you see setting
 * has, geometrically, already set. Applied at the *render* boundary rather than inside the
 * position, since it is a property of the air rather than of the orbit.
 */
export function refractionDeg(apparentElevationDeg: number): number {
  if (apparentElevationDeg < -2) return 0;
  const h = Math.max(apparentElevationDeg, -0.5);
  return 1 / (60 * Math.tan((h + 7.31 / (h + 4.4)) * DEG));
}

/**
 * A unit vector toward a body, in the render frame.
 *
 * Render space at the floating origin is Y-up, X-east, Z-south (nose down −Z), which is the
 * convention the whole renderer already uses — see `sim/frames.ts`. Azimuth is from north
 * toward east, so north is −Z and east is +X.
 */
export function horizontalToRenderVector(
  position: HorizontalPosition,
  out: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  const elevation = position.elevationDeg * DEG;
  const azimuth = position.azimuthDeg * DEG;
  const horizontal = Math.cos(elevation);
  out.x = horizontal * Math.sin(azimuth); // east
  out.y = Math.sin(elevation); // up
  out.z = -horizontal * Math.cos(azimuth); // north is −Z
  return out;
}

function normaliseDeg(degrees: number): number {
  const wrapped = degrees % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}
